import axios from "axios";
import { createStore } from "vuex";
import { setRequestHeaders } from "@/lib/api";
import { AuthLocalStorage, setAuth } from "@/lib/auth";

import API from "@/api-client";
import { Appointment } from "@/typings/appointment";
import { AppointmentReport } from "@/typings/appointmentReport";
import { AuthRole } from "@/typings/auth";
import { Customer } from "@/typings/customer";
import { Invoice } from "@/typings/invoice";
import { Job } from "@/typings/job";
import { Tenant, TenantCustomizations } from "@/typings/tenant";
import actions from "./actions";
import { formatDate } from "./lib/date";

export default createStore({
  state: {
    globalErrorMessage: "",
    appointment: null as Appointment | null,
    currentQuote: null,
    currentInvoice: null,
    availableDates: [],
    customizations: {},
    equipment: [],
    fetchedMonths: {},
    invoices: [] as Invoice[],
    job: null as Job | null,
    quotes: [],
    recentAppointments: [] as AppointmentReport[],
    recentAppointmentsLoaded: false,
    serviceCategories: [],
    tenant: null as (Tenant & TenantCustomizations) | null,
    timeSlots: [],
    user: null as Customer | null,
    userAppointments: [],
    userAppointmentsLoaded: false
  },
  mutations: {
    updateState(state, payload) {
      state[payload.state] = {
        ...state[payload.state],
        ...payload.data
      };
    },
    replaceState(state, payload) {
      state[payload.state] = payload.data;
    }
  },
  actions: {
    ...actions,
    setGlobalErrorMessage({ commit }, message) {
      commit("replaceState", {
        state: "globalErrorMessage",
        data: message
      });
    },
    async fetchAppointment({ dispatch, commit }, id) {
      return API.appointment.get(id).then(async (res) => {
        commit("replaceState", {
          state: "appointment",
          data: res.data
        });
        await dispatch("fetchJob", res.data.job_id);
        return res;
      });
    },
    async fetchJob({ commit }, jobId) {
      return API.makeRequest(`/v2/jobs/${jobId}`, {
        method: "GET",
        params: { item_grouping: 1 }
      }).then((res) => {
        commit("replaceState", {
          state: "job",
          data: res.data
        });
      });
    },
    async fetchQuote({ commit }, quoteId) {
      return API.makeRequest(`/v2/quotes/${quoteId}`, {
        method: "GET",
        params: { item_grouping: 1 }
      }).then((res) => {
        commit("replaceState", {
          state: "currentQuote",
          data: res.data
        });
      });
    },
    async fetchInvoice({ commit }, invoiceId) {
      return API.makeRequest(`/v2/invoices/${invoiceId}`, {
        method: "GET",
        params: { item_grouping: 1 }
      }).then((res) => {
        commit("replaceState", {
          state: "currentInvoice",
          data: res.data
        });
      });
    },
    fetchInvoices({ commit }, customerId) {
      return API.makeRequest(`/v2/customers/${customerId}/invoices`, {
        method: "GET",
        params: { item_grouping: 1 }
      }).then((res) => {
        commit("replaceState", {
          state: "invoices",
          data: res.data
        });
      });
    },
    fetchQuotes({ commit }, customerId) {
      return API.makeRequest(`/v2/customers/${customerId}/quotes`, {
        method: "GET",
        params: { item_grouping: 1 }
      }).then((res) => {
        commit("replaceState", {
          state: "quotes",
          data: res.data
        });
      });
    },
    fetchAvailableDates({ commit, state }, params) {
      return new Promise((resolve) => {
        const { date, category, zipCode } = params;
        const monthKey = `${date.getFullYear()}-${date.getMonth() + 1}`;
        // Check for month in existing cache
        const cachedKey = Object.keys(state.fetchedMonths).find(
          (key) => key === monthKey
        );
        // Only fetch months once
        if (!cachedKey) {
          API.availability
            .getAvailableDates({
              service_date: formatDate(date),
              expected_duration: category.duration,
              service_category_id: category.id,
              postal_code: zipCode
            })
            .then((res) => {
              commit({
                type: "replaceState",
                state: "availableDates",
                data: { ...res.data }
              });
              // Add month to cache
              commit("updateState", {
                state: "fetchedMonths",
                data: { [monthKey]: res.data }
              });
              resolve(void 0);
            });
        } else {
          // Rehydrate from cache
          commit({
            type: "replaceState",
            state: "availableDates",
            data: state.fetchedMonths[cachedKey]
          });
          resolve(void 0);
        }
      });
    },
    fetchTimeSlots({ commit }, params) {
      const { date, category, zipCode } = params;
      return API.availability
        .getAvailableTimeSlots({
          service_date: formatDate(date),
          expected_duration: category.duration,
          service_category_id: category.id,
          postal_code: zipCode
        })
        .then((res) => {
          commit({
            type: "replaceState",
            state: "timeSlots",
            data: res.data
          });
        });
    },
    fetchUserAppointments({ commit, state }) {
      return API.user.getAllAppointments(state.user!.id).then((res) => {
        commit("replaceState", {
          state: "userAppointments",
          data: res.data
        });
        commit("replaceState", {
          state: "userAppointmentsLoaded",
          data: true
        });
      });
    },
    fetchRecentAppointments({ commit }) {
      const reportPath = "/reports/consumer/appointments.json";
      const reportParams = {
        page: 1,
        per_page: 10,
        sort_by: "date",
        sort_desc: true
      };
      return API.makeRequest(reportPath, {
        method: "GET",
        params: reportParams
      }).then((res) => {
        commit("replaceState", {
          state: "recentAppointments",
          data: res.data.data
        });
        commit("replaceState", {
          state: "recentAppointmentsLoaded",
          data: true
        });
      });
    },
    quoteProceed(_, { link, data }) {
      return API.makeRequest(link, {
        method: "POST",
        data
      });
    },
    createAppointment(_, params) {
      // Add defaults to payload
      const data = { ...params };

      // Add promo code to notes
      if (data.promoCode.length) {
        data.details.notes = `PROMO CODE: ${data.promoCode}<br>--------------------<br>${data.details.notes}`;
      }
      delete data.promoCode;

      return API.appointment.create({ appointment: data });
    },
    reserveS3Photos(_, payload) {
      const { job, extensions } = payload;

      return API.makeRequest(job.links.reserve_s3_photos.href, {
        method: "POST",
        data: { extensions }
      });
    },
    async uploadS3Photo(_, payload) {
      const { file, reservation } = payload;

      const formData = new FormData();
      Object.keys(reservation.parameters).forEach((key) => {
        formData.append(key, reservation.parameters[key]);
      });
      formData.append("file", file);

      // post photo to S3
      const s3Axios = axios.create();
      await s3Axios.post(reservation.server_url, formData);

      // finalize with backend API
      return API.makeRequest(reservation.success_url, {
        method: "POST",
        data: { source: "customer_portal" }
      });
    },
    fetchUser({ dispatch }) {
      return API.user.findByEmail(AuthLocalStorage.email).then((res) => {
        dispatch("setUser", res.data);
      });
    },
    setUser({ commit }, user: Customer | null) {
      if (user) {
        setAuth(user);
        if (user.authentication_token) {
          setRequestHeaders({ ...user, id: undefined });
        }
      } else {
        AuthLocalStorage.clear();
        setRequestHeaders(null);
      }
      commit("replaceState", {
        state: "user",
        data: user
      });
    }
  },
  getters: {
    isCustomer(state) {
      return state.user?.role === AuthRole.customer;
    },
    primaryAddress(state) {
      return state.user?.addresses?.find((a) => a.primary) || {};
    },
    isDevelopment() {
      return import.meta.env.VITE_ENV === "development";
    }
  }
});
